import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table, Tab, Tabs, Modal } from 'react-bootstrap';
import { BsSearch, BsXLg } from 'react-icons/bs';
import { FluxoAprovacaoPedidoStatus } from '../../../@types/enums/fluxo-aprovacao-pedido-status-enum';
import { FaFileDownload, FaPencilAlt, FaRegEye } from 'react-icons/fa';
import { TableHeaderStyled } from 'pages/style/table/table-header';
import { PedidoResumo } from '../../../@types/entity/pedido-resumo-entity';
import {
  getCodigoItem,
  getStatusBadge,
  getStatusLabel,
  podeBaixarCsv,
  MENSAGEM_CSV_BLOQUEADO,
  TAMANHO_PAGINA_LISTAGEM_PEDIDOS,
  fatiaPagina,
  totalPaginasLista,
  listaTemPrecoCampanha,
} from 'helper/fluxo-aprovacao-pedido-helper';
import { ParseToCurrencyBR } from 'helper/format-currency';
import { PedidoVersaoDetalhe } from '../../../@types/entity/pedido-versao-detalhe-entity';
import { FluxoAprovacaoPedidoConsultaAprovadores } from './components/fluxo-aprovacao-pedido-consulta-aprovadores';
import '../../style/analysis/orders-intentions-for-approval.css';


export type AbaPedido = 'analitico' | 'sintetico' | 'campanhas' | 'aprovadores';

type Props = {
  analiticos: PedidoResumo[];
  /** FU01: Sintético consome o fluxo novo (mesmas ações do Analítico). */
  sinteticos: PedidoResumo[];
  /** FU02: Campanhas consome o fluxo novo (mesmas ações do Analítico). */
  campanhas: PedidoResumo[];
  dataInicio: string;
  dataFim: string;
  onDataInicio: (v: string) => void;
  onDataFim: (v: string) => void;
  onFiltrar: () => void;
  onAbrir: (idPedido: number) => void;
  onBaixarCsv: (aba: AbaPedido, id: number) => void;
  /** Carrega TODAS as versões do pedido (cada uma com os seus itens) para a modal. */
  onCarregarVersoes?: (idPedido: number) => Promise<PedidoVersaoDetalhe[]>;
};

/**
 * Tag de status com a paleta central (`getStatusBadge`) — a mesma cor representa
 * o mesmo estado aqui e no detalhe do pedido. O `text` vem do helper porque o badge do Bootstrap
 * usa texto branco por padrão, ilegível sobre os fundos claros (âmbar/ciano).
 */
function renderBadgeStatus(status: PedidoResumo['status']) {
  const estilo = getStatusBadge(status);
  return (
    <Badge bg={estilo.bg} text={estilo.text}>
      {getStatusLabel(status)}
    </Badge>
  );
}

export function FluxoAprovacaoPedidoListaPage(props: Props) {
  const { analiticos, sinteticos, campanhas, dataInicio, dataFim, onCarregarVersoes } = props;
  const { onDataInicio, onDataFim, onFiltrar, onAbrir, onBaixarCsv } = props;

  const [abaAtiva, setAbaAtiva] = useState<AbaPedido>(abaInicial());
  const [mostrarModalVersao, setMostrarModalVersao] = useState(false);
  const [versoes, setVersoes] = useState<PedidoVersaoDetalhe[]>([]);
  const [carregandoVersao, setCarregandoVersao] = useState(false);

  const [filtroFilial, setFiltroFilial] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const todosPedidos = useMemo(
    () => [...analiticos, ...sinteticos, ...campanhas],
    [analiticos, sinteticos, campanhas]
  );

  const opcoesFiliais = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of todosPedidos) {
      const chave = p.nomeFilial ? p.nomeFilial.trim() : p.idFilial ? String(p.idFilial) : '';
      if (chave) {
        map.set(chave, p.nomeFilial || `Filial ${p.idFilial}`);
      }
    }
    return Array.from(map.entries())
      .map(([valor, label]) => ({ valor, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [todosPedidos]);

  const opcoesStatus = useMemo(() => {
    const set = new Set<FluxoAprovacaoPedidoStatus>();
    for (const p of todosPedidos) {
      if (p.status) {
        set.add(p.status);
      }
    }
    return Array.from(set)
      .map((status) => ({ valor: status, label: getStatusLabel(status) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [todosPedidos]);

  function aplicarFiltros(lista: PedidoResumo[]): PedidoResumo[] {
    return lista.filter((p) => {
      if (filtroFilial) {
        const chaveFilial = p.nomeFilial ? p.nomeFilial.trim() : p.idFilial ? String(p.idFilial) : '';
        if (chaveFilial !== filtroFilial) {
          return false;
        }
      }
      if (filtroStatus) {
        if (p.status !== filtroStatus) {
          return false;
        }
      }
      return true;
    });
  }

  const analiticosFiltrados = useMemo(() => aplicarFiltros(analiticos), [analiticos, filtroFilial, filtroStatus]);
  const sinteticosFiltrados = useMemo(() => aplicarFiltros(sinteticos), [sinteticos, filtroFilial, filtroStatus]);
  const campanhasFiltradas = useMemo(() => aplicarFiltros(campanhas), [campanhas, filtroFilial, filtroStatus]);

  function limparFiltros() {
    setFiltroFilial('');
    setFiltroStatus('');
  }

  const temFiltroAtivo = filtroFilial !== '' || filtroStatus !== '';

  /** A modal abre com o histórico COMPLETO de versões, não só a versão clicada. */
  async function abrirModalVersao(idPedido: number) {
    if (!onCarregarVersoes) {
      return;
    }
    setCarregandoVersao(true);
    setMostrarModalVersao(true);
    try {
      setVersoes(await onCarregarVersoes(idPedido));
    } finally {
      setCarregandoVersao(false);
    }
  }

  function fecharModalVersao() {
    setMostrarModalVersao(false);
    setVersoes([]);
  }

  return (
    <>
      <Card>
        <Card.Body>
          {abaAtiva !== 'aprovadores' && (
            <>
              <Row className="align-items-end g-2">
                <Col sm={6} md={3} lg={2}>
                  <Form.Group controlId="dataInicio">
                    <Form.Label className="small fw-bold text-secondary mb-1">Data Início:</Form.Label>
                    <Form.Control size="sm" type="date" value={dataInicio} onChange={(e) => onDataInicio(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col sm={6} md={3} lg={2}>
                  <Form.Group controlId="dataFim">
                    <Form.Label className="small fw-bold text-secondary mb-1">Data Fim:</Form.Label>
                    <Form.Control size="sm" type="date" value={dataFim} onChange={(e) => onDataFim(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col sm={6} md={3} lg={4}>
                  <Form.Group controlId="filtroFilial">
                    <Form.Label className="small fw-bold text-secondary mb-1">Filial:</Form.Label>
                    <Form.Select
                      size="sm"
                      aria-label="Filtrar por Filial"
                      value={filtroFilial}
                      onChange={(e) => setFiltroFilial(e.target.value)}
                    >
                      <option value="">Todas as filiais</option>
                      {opcoesFiliais.map((f) => (
                        <option key={f.valor} value={f.valor}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm={6} md={3} lg={3}>
                  <Form.Group controlId="filtroStatus">
                    <Form.Label className="small fw-bold text-secondary mb-1">Status:</Form.Label>
                    <Form.Select
                      size="sm"
                      aria-label="Filtrar por Status"
                      value={filtroStatus}
                      onChange={(e) => setFiltroStatus(e.target.value)}
                    >
                      <option value="">Todos os status</option>
                      {opcoesStatus.map((s) => (
                        <option key={s.valor} value={s.valor}>
                          {s.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm={6} md={3} lg={1} className="d-flex gap-1">
                  <Button
                    size="sm"
                    variant="primary"
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={onFiltrar}
                    aria-label="Filtrar"
                    title="Filtrar no período"
                  >
                    <BsSearch className="me-1" /> Filtrar
                  </Button>
                  {temFiltroAtivo && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={limparFiltros}
                      aria-label="Limpar filtros"
                      title="Limpar filtros de filial, marca e status"
                    >
                      <BsXLg />
                    </Button>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: '10px', borderBottom: '1px solid #6C757D' }}></Row>
            </>
          )}

          <Tabs
            activeKey={abaAtiva}
            onSelect={(chave) => setAbaAtiva((chave as AbaPedido) ?? 'analitico')}
            className="mt-3"
            id="abas-fluxo-aprovacao"
          >
            <Tab eventKey="analitico" title="Analítico">
              <NovoFluxoTabela aba="analitico" pedidos={analiticosFiltrados} onAbrir={onAbrir} onBaixarCsv={onBaixarCsv} onAbrirVersao={abrirModalVersao} />
            </Tab>
            <Tab eventKey="sintetico" title="Sintético">
              <NovoFluxoTabela aba="sintetico" pedidos={sinteticosFiltrados} onAbrir={onAbrir} onBaixarCsv={onBaixarCsv} onAbrirVersao={abrirModalVersao} />
            </Tab>
            <Tab eventKey="campanhas" title="Campanhas">
              <NovoFluxoTabela aba="campanhas" pedidos={campanhasFiltradas} onAbrir={onAbrir} onBaixarCsv={onBaixarCsv} onAbrirVersao={abrirModalVersao} />
            </Tab>
            <Tab eventKey="aprovadores" title="Aprovadores" mountOnEnter unmountOnExit>
              <FluxoAprovacaoPedidoConsultaAprovadores />
            </Tab>
          </Tabs>

          <Modal show={mostrarModalVersao} onHide={fecharModalVersao} size="xl" dialogClassName="modal-95w">
            <Modal.Header closeButton>
              <Modal.Title>Versões do pedido</Modal.Title>
            </Modal.Header>
            <Modal.Body data-testid="modal-versoes">
              {carregandoVersao ? (
                <div className="text-muted">Carregando versões do pedido...</div>
              ) : (
                renderVersoes(versoes)
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={fecharModalVersao}>
                Fechar
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>
    </>
  );
}

const ABAS_VALIDAS: AbaPedido[] = ['analitico', 'sintetico', 'campanhas', 'aprovadores'];

/** Aba inicial vinda da origem do pedido (?aba=sintetico|campanhas|analitico); padrão: Analítico. */
function abaInicial(): AbaPedido {
  const aba = new URLSearchParams(window.location.search).get('aba') as AbaPedido | null;
  return aba && ABAS_VALIDAS.includes(aba) ? aba : 'analitico';
}

type NovoFluxoProps = {
  aba: AbaPedido;
  pedidos: PedidoResumo[];
  onAbrir: (idPedido: number) => void;
  onBaixarCsv: (aba: AbaPedido, id: number) => void;
  onAbrirVersao?: (idPedido: number) => void;
};

/** Tabela dos pedidos do fluxo novo (Analítico, Sintético e Campanhas): abrir detalhe/ações e baixar CSV. */
function NovoFluxoTabela({ aba, pedidos, onAbrir, onBaixarCsv, onAbrirVersao }: NovoFluxoProps) {
  const [pagina, setPagina] = useState(0);
  useEffect(() => {
    setPagina(0);
  }, [pedidos]);
  if (pedidos.length === 0) {
    return <div className="text-muted mt-3">Nenhum pedido encontrado.</div>;
  }
  const paginas = totalPaginasLista(pedidos.length);
  const paginaAtual = Math.min(pagina, paginas - 1);
  const visiveis = fatiaPagina(pedidos, paginaAtual);
  return (
    <>
      <TableHeaderStyled>
        <Table striped bordered hover responsive className="mt-3" data-testid={`tabela-${aba}`}>
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Filial</th>
              <th>Versão</th>
              <th>Status</th>
              <th>Cadastro</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((pedido) => renderLinhaPedido(pedido, aba, onAbrir, onBaixarCsv, onAbrirVersao))}
          </tbody>
        </Table>
      </TableHeaderStyled>
      <PaginacaoAba aba={aba} pagina={paginaAtual} paginas={paginas} tamanho={TAMANHO_PAGINA_LISTAGEM_PEDIDOS} onChange={setPagina} />
    </>
  );
}

function PaginacaoAba({
  aba,
  pagina,
  paginas,
  tamanho,
  onChange,
}: {
  aba: AbaPedido;
  pagina: number;
  paginas: number;
  tamanho: number;
  onChange: (pagina: number) => void;
}) {
  if (paginas <= 1) {
    return null;
  }
  return (
    <div className="d-flex align-items-center gap-2 mt-2" data-testid={`paginacao-${aba}`}>
      <Button size="sm" variant="outline-secondary" disabled={pagina === 0} onClick={() => onChange(pagina - 1)}>
        Anterior
      </Button>
      <span>
        {pagina + 1} / {paginas} ({tamanho} por página)
      </span>
      <Button size="sm" variant="outline-secondary" disabled={pagina >= paginas - 1} onClick={() => onChange(pagina + 1)}>
        Próxima
      </Button>
    </div>
  );
}

/** Linha da lista: identificação do pedido + ações (abrir detalhe, versões e CSV). */
function renderLinhaPedido(
  pedido: PedidoResumo,
  aba: AbaPedido,
  onAbrir: (idPedido: number) => void,
  onBaixarCsv: (aba: AbaPedido, id: number) => void,
  onAbrirVersao?: (idPedido: number) => void
) {
  // O CSV só é liberado depois da aprovação conjunta; antes disso o botão fica
  // desabilitado e o title informa o motivo (a linha da tabela não comporta o texto de bloqueio).
  const csvLiberado = podeBaixarCsv(pedido.status);
  const pedidoGerado = pedido.status === FluxoAprovacaoPedidoStatus.PEDIDO_GERADO;
  return (
    <tr key={pedido.idPedido}>
      <td
        title={String(pedido.nome ?? pedido.idPedido)}
        style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {pedido.nome ?? pedido.idPedido}
      </td>
      <td>{pedido.nomeFilial ?? pedido.idFilial ?? '-'}</td>
      <td>
        {pedido.versaoAtual ? (
          onAbrirVersao ? (
            <a
              href="#"
              aria-label={`ver-versoes-${pedido.idPedido}`}
              onClick={(e) => {
                e.preventDefault();
                onAbrirVersao(pedido.idPedido);
              }}
              title="Clique para ver os itens de cada versão"
            >
              Versão {pedido.versaoAtual}
            </a>
          ) : (
            `Versão ${pedido.versaoAtual}`
          )
        ) : (
          '-'
        )}
      </td>
      <td>{renderBadgeStatus(pedido.status)}</td>
      <td>{pedido.dataCadastro ?? '-'}</td>
      <td style={{ textAlign: 'center', whiteSpace: 'nowrap', minWidth: '96px' }}>
        <div className="d-inline-flex flex-nowrap align-items-center justify-content-center" style={{ gap: '4px' }}>
          <Button
            variant="primary"
            size="sm"
            className="flex-shrink-0"
            title={pedidoGerado ? 'Visualizar pedido' : 'Abrir pedido'}
            aria-label="Abrir"
            onClick={() => onAbrir(pedido.idPedido)}
          >
            {pedidoGerado ? <FaRegEye /> : <FaPencilAlt />}
          </Button>
          <Button
            variant="success"
            size="sm"
            className="flex-shrink-0"
            title={csvLiberado ? 'Baixar CSV' : MENSAGEM_CSV_BLOQUEADO}
            aria-label="Baixar CSV"
            disabled={!csvLiberado}
            onClick={() => onBaixarCsv(aba, pedido.idPedido)}
          >
            <FaFileDownload />
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Uma seção por versão, cada uma com a sua própria listagem de itens — assim dá
 * para comparar, por exemplo, o item A com qtd 10 na versão 1 e qtd 11 na versão 2.
 */
function renderVersoes(versoes: PedidoVersaoDetalhe[]) {
  if (versoes.length === 0) {
    return <div className="text-muted">Nenhuma versão disponível.</div>;
  }
  return (
    <>
      {versoes.map((versao) => (
        <div className="mb-4" key={versao.numeroVersao} data-testid={`versao-${versao.numeroVersao}`}>
          <h6>Versão {versao.numeroVersao}</h6>
          <div className="text-muted small mb-2">
            Criada em {versao.dataCriacao ?? '-'} por {versao.autor ?? '-'}
          </div>
          {renderTabelaItensVersao(versao)}
        </div>
      ))}
    </>
  );
}

function formatarValor(valor?: number): string {
  if (valor === undefined || valor === null) {
    return '-';
  }
  return ParseToCurrencyBR(valor);
}

function renderTabelaItensVersao(detalhe: PedidoVersaoDetalhe) {
  if (!detalhe.itens || detalhe.itens.length === 0) {
    return <div className="text-muted">Nenhum item nesta versão.</div>;
  }
  const campanha = listaTemPrecoCampanha(detalhe.itens);
  return (
    <TableHeaderStyled>
      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>Descrição</th>
            <th>Código Fabricante</th>
            <th>Qtd</th>
            <th>Qme</th>
            <th>Preço reposição</th>
            <th>Custo Médio</th>
            <th>Preço pedido</th>
            <th>Cvf</th>
            <th>Ncvf</th>
            <th>Pontuação</th>
            <th>Média demanda</th>
            <th>Mês Atual</th>
            <th>Demandas M-1</th>
            <th>Demandas M-2</th>
            <th>Demandas M-3</th>
            <th>Demandas M-4</th>
            <th>Demandas M-5</th>
            <th>Estoque</th>
            <th>Bloqueadas</th>
            <th>Pendentes</th>
            {campanha && <th>Preco sem ST</th>}
            {campanha && <th>Preco Sem ST com Desconto</th>}
            {campanha && <th>Preco com ST</th>}
            {campanha && <th>Totalizador preco sem ST</th>}
            {campanha && <th>Totalizador preco sem ST com desconto</th>}
            {campanha && <th>Totalizador preco com ST</th>}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {detalhe.itens.map((item, idx) => (
            <tr key={`${item.idItem}-${idx}`}>
              <td>{item.descricao ?? '-'}</td>
              <td>{getCodigoItem(item)}</td>
              <td>{item.quantidade}</td>
              <td>{item.qme ?? '-'}</td>
              <td>{formatarValor(item.precoReposicao)}</td>
              <td>{formatarValor(item.custoMedio)}</td>
              <td>{formatarValor(item.precoPedido)}</td>
              <td>{item.cvf ?? '-'}</td>
              <td>{item.ncvf ?? '-'}</td>
              <td>{item.pontuacao ?? '-'}</td>
              <td>{item.mediaDemanda !== undefined && item.mediaDemanda !== null ? item.mediaDemanda.toFixed(2) : '-'}</td>
              <td>{item.demandaMesAtual ?? '-'}</td>
              <td>{item.demandaMes1 ?? '-'}</td>
              <td>{item.demandaMes2 ?? '-'}</td>
              <td>{item.demandaMes3 ?? '-'}</td>
              <td>{item.demandaMes4 ?? '-'}</td>
              <td>{item.demandaMes5 ?? '-'}</td>
              <td>{item.estoque ?? '-'}</td>
              <td>{item.bloqueadas ?? '-'}</td>
              <td>{item.pendentes ?? '-'}</td>
              {campanha && <td>{formatarPrecoCampanha(item.precoSemSt)}</td>}
              {campanha && <td>{formatarPrecoCampanha(item.precoSemStDesconto)}</td>}
              {campanha && <td>{formatarPrecoCampanha(item.precoComSt)}</td>}
              {campanha && <td>{formatarPrecoCampanha(item.totalizadorPrecoSemSt)}</td>}
              {campanha && <td>{formatarPrecoCampanha(item.totalizadorPrecoSemStDesconto)}</td>}
              {campanha && <td>{formatarPrecoCampanha(item.totalizadorPrecoComSt)}</td>}
              <td>{item.statusItem ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableHeaderStyled>
  );
}

function formatarPrecoCampanha(valor?: number | null): string {
  if (valor === undefined || valor === null) {
    return '-';
  }
  return ParseToCurrencyBR(valor);
}

export default FluxoAprovacaoPedidoListaPage;
